import {
  doc,
  collection,
  runTransaction,
  serverTimestamp,
  writeBatch,
  getDocs,
} from "firebase/firestore";
import { db } from "../services/firebase";

function stockDocId(locationId, itemId) {
  return `${locationId}__${itemId}`;
}
// Util
const stockKey = (locId, itemId) => `${locId}__${itemId}`;

const sum = (arr) => arr.reduce((s, n) => s + Number(n || 0), 0);

export async function commitSaleAndStock({
  sale,            // { items:[{itemId, qty, price, ...}], grandTotal, payments:[{method, amount}], ...}
  locationId,      // ex: "boutique"
  cashierId,       // ex: auth.uid
  sessionId = null // id de cash_sessions/* ou null
}) {
  if (!sale?.items?.length) throw new Error("Vente vide.");

  const saleRef = doc(collection(db, "sales"));
  const cashSessionRef = sessionId ? doc(db, "cash_sessions", sessionId) : null;

  const itemIds = Array.from(new Set(sale.items.map(it => it.itemId)));
  const stockRefs = itemIds.map(id => doc(db, "stock", stockKey(locationId, id)));
  const aggRefs   = itemIds.map(id => doc(db, "stockAgg", id));

  // ===== Calculs de paiement (IMPORTANT) =====
  const total = Number(sale.grandTotal || 0);

  const cashTendered = sum((sale.payments || [])
    .filter(p => (p.method || "").toLowerCase() === "cash")
    .map(p => p.amount));

  const nonCashTendered = sum((sale.payments || [])
    .filter(p => (p.method || "").toLowerCase() !== "cash")
    .map(p => p.amount));

  // montant non-cash appliqué à la facture (plafonné au total)
  const nonCashApplied = Math.min(total, nonCashTendered);

  // reste après avoir appliqué le non-cash
  const remainingAfterNonCash = Math.max(0, total - nonCashApplied);

  // part CASH réellement appliquée au règlement (plafonnée au reste ET à l'espèce donnée)
  const cashApplied = Math.min(remainingAfterNonCash, cashTendered);

  // rendu monnaie (si le client a donné plus que nécessaire)
  const changeOut = Math.max(0, cashTendered - cashApplied);

  await runTransaction(db, async (tx) => {
    // ---------- LECTURES ----------
    const stockSnaps = await Promise.all(stockRefs.map(r => tx.get(r)));
    const aggSnaps   = await Promise.all(aggRefs.map(r => tx.get(r)));

    let sessionSnap = null;
    if (cashSessionRef) {
      sessionSnap = await tx.get(cashSessionRef);
      if (!sessionSnap.exists()) throw new Error("Session de caisse introuvable.");
      if ((sessionSnap.data()?.status || "").toLowerCase() !== "open") {
        throw new Error("La session de caisse n’est pas ouverte.");
      }
    }

    // Vérif simple des quantités (option stricte : bloquer si insuffisant)
    const needById = new Map();
    sale.items.forEach(it => {
      needById.set(it.itemId, (needById.get(it.itemId) || 0) + Number(it.qty || 0));
    });

    itemIds.forEach((id, i) => {
      const need = needById.get(id) || 0;
      // const have = (stockSnaps[i].exists() ? stockSnaps[i].data().qty : 0) || 0;
      if (need < 0 || !Number.isFinite(need)) throw new Error("Quantité invalide.");
      // if (have < need) throw new Error(`Stock insuffisant pour ${id} (dispo: ${have}, demandé: ${need})`);
    });

    // ---------- CALCULS ----------
    const newStockQty = new Map();
    const newAggMap   = new Map();

    itemIds.forEach((id, i) => {
      const need = needById.get(id) || 0;

      const curStock = (stockSnaps[i].exists() ? stockSnaps[i].data().qty : 0) || 0;
      newStockQty.set(id, curStock - need);

      const curAgg = aggSnaps[i].exists()
        ? aggSnaps[i].data()
        : { total: 0, perLocation: {}, onOrder: 0 };

      const perLoc = { ...(curAgg.perLocation || {}) };
      const curLoc = Number(perLoc[locationId] || 0);
      perLoc[locationId] = curLoc - need;

      newAggMap.set(id, {
        total: Number(curAgg.total || 0) - need,
        onOrder: Number(curAgg.onOrder || 0),
        perLocation: perLoc,
      });
    });

    // ---------- ÉCRITURES ----------
    // 1) vente
    tx.set(saleRef, {
      locationId,
      cashierId,
      sessionId: sessionId || null,
      items: sale.items,
      subTotal: Number(sale.subTotal || 0),
      taxTotal: Number(sale.taxTotal || 0),
      discountTotal: Number(sale.discountTotal || 0),
      grandTotal: total,
      payments: (sale.payments || []).map(p => ({
        method: (p.method || "").toLowerCase(),
        amount: Number(p.amount || 0),
        ref: p.ref || null,
      })),
      // Pour audit, on peut stocker ce qui a été effectivement appliqué
      applied: {
        nonCashApplied,
        cashApplied,
        changeOut,
      },
      createdAt: serverTimestamp(),
    });

    // 2) stock/aggregats
    itemIds.forEach((id, i) => {
      const sRef = stockRefs[i];
      const aRef = aggRefs[i];
      const qty  = newStockQty.get(id);
      const agg  = newAggMap.get(id);

      tx.set(sRef, {
        locationId,
        itemId: id,
        qty,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      tx.set(aRef, {
        total: agg.total,
        onOrder: agg.onOrder,
        perLocation: agg.perLocation,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    });

    // 3) mouvements de caisse (SEULEMENT ce qui doit vraiment impacter la caisse)
    if (cashSessionRef) {
      // a) entrée d'espèces = cashApplied (montant de la facture payé en cash)
      if (cashApplied > 0) {
        const movInRef = doc(collection(db, "cash_moves"));
        tx.set(movInRef, {
          sessionId,
          locationId,
          type: "sale_in",
          amount: cashApplied,          // ✅ on ajoute 2400 ici (dans ton exemple)
          saleId: saleRef.id,
          at: serverTimestamp(),
        });
      }
      // b) rendu monnaie = changeOut (mouvement négatif)
      if (changeOut > 0) {
        const movOutRef = doc(collection(db, "cash_moves"));
        tx.set(movOutRef, {
          sessionId,
          locationId,
          type: "change_out",
          amount: -changeOut,           // ✅ on retire -100 ici (dans ton exemple)
          saleId: saleRef.id,
          at: serverTimestamp(),
        });
      }
    }
  });

  return { saleId: saleRef.id };
}

/**
 * --- RÉCEPTION D'ACHAT ---
 */
export async function receivePurchase({ locationId, items, poId }) {
  const stockRefs = items.map((i) => doc(db, "stock", stockDocId(locationId, i.itemId)));
  const poRef = poId ? doc(db, "purchaseOrders", poId) : null;

  await runTransaction(db, async (tx) => {
    // 1️⃣ READS EN PREMIER
    const stockDocs = await Promise.all(stockRefs.map((r) => tx.get(r)));
    const poSnap = poRef ? await tx.get(poRef) : null;

    // 2️⃣ WRITES APRÈS TOUTES LES LECTURES
    for (let i = 0; i < items.length; i++) {
      const { itemId, qty } = items[i];
      const sRef = stockRefs[i];
      const sDoc = stockDocs[i];
      const currentQty = (sDoc.exists() ? sDoc.data().qty : 0) || 0;
      const newQty = currentQty + Math.abs(Number(qty || 0));

      tx.set(
        sRef,
        { locationId, itemId, qty: newQty, updatedAt: serverTimestamp() },
        { merge: true }
      );

      const movRef = doc(collection(db, "inventoryMoves"));
      tx.set(movRef, {
        type: "purchase",
        locationId,
        poId: poId || null,
        itemId,
        qty: Math.abs(Number(qty || 0)),
        at: serverTimestamp(),
      });
    }

    // Met à jour le PO si fourni
    if (poRef && poSnap?.exists()) {
      const po = poSnap.data();
      const lines = po.items || [];
      const incById = new Map();
      for (const { itemId, qty } of items) {
        incById.set(itemId, (incById.get(itemId) || 0) + Math.abs(qty));
      }

      const updated = lines.map((l) => {
        const ordered = Number(l.qtyOrdered || l.qty || 0);
        const received = Math.min(
          ordered,
          Number(l.qtyReceived || 0) + (incById.get(l.itemId) || 0)
        );
        return { ...l, qtyOrdered: ordered, qtyReceived: received };
      });

      const status = updated.every((l) => l.qtyReceived >= l.qtyOrdered)
        ? "closed"
        : "partial";

      tx.set(
        poRef,
        { items: updated, status, updatedAt: serverTimestamp() },
        { merge: true }
      );
    }
  });
}

/**
 * --- TRANSFERT ---
 */
export async function transferStock({ from, to, items }) {
  const fromRefs = items.map((l) => doc(db, "stock", stockDocId(from, l.itemId)));
  const toRefs = items.map((l) => doc(db, "stock", stockDocId(to, l.itemId)));

  await runTransaction(db, async (tx) => {
    // 1️⃣ READS
    const fromDocs = await Promise.all(fromRefs.map((r) => tx.get(r)));
    const toDocs = await Promise.all(toRefs.map((r) => tx.get(r)));

    // 2️⃣ Vérif
    items.forEach((l, i) => {
      const current = (fromDocs[i].exists() ? fromDocs[i].data().qty : 0) || 0;
      if (current < l.qty) {
        throw new Error(
          `Stock insuffisant à "${from}" pour ${l.itemId} (dispo: ${current}, demandé: ${l.qty})`
        );
      }
    });

    // 3️⃣ WRITES
    for (let i = 0; i < items.length; i++) {
      const { itemId, qty } = items[i];

      const fromQty = (fromDocs[i].exists() ? fromDocs[i].data().qty : 0) || 0;
      const toQty = (toDocs[i].exists() ? toDocs[i].data().qty : 0) || 0;

      tx.set(
        fromRefs[i],
        { locationId: from, itemId, qty: fromQty - qty, updatedAt: serverTimestamp() },
        { merge: true }
      );
      tx.set(
        toRefs[i],
        { locationId: to, itemId, qty: toQty + qty, updatedAt: serverTimestamp() },
        { merge: true }
      );

      const movOut = doc(collection(db, "inventoryMoves"));
      tx.set(movOut, {
        type: "transfer-out",
        from,
        to,
        itemId,
        qty: -Math.abs(qty),
        at: serverTimestamp(),
      });

      const movIn = doc(collection(db, "inventoryMoves"));
      tx.set(movIn, {
        type: "transfer-in",
        from,
        to,
        itemId,
        qty: Math.abs(qty),
        at: serverTimestamp(),
      });
    }
  });
}

/**
 * --- INITIALISATION STOCK 0 ---
 */
export async function seedZeroStock(locationId = "boutique") {
  const snap = await getDocs(collection(db, "itemsEK"));
  const batch = writeBatch(db);
  snap.forEach((d) => {
    const sRef = doc(db, "stock", stockDocId(locationId, d.id));
    batch.set(
      sRef,
      { locationId, itemId: d.id, qty: 0, updatedAt: serverTimestamp() },
      { merge: true }
    );
  });
  await batch.commit();
}
